// The design tradition MDS continues. Every statement links to its source.

export type HeritageArt =
  | 'flag13'
  | 'flag48'
  | 'park'
  | 'orbit'
  | 'uswds'
  | 'usds'
  | 'seal'
  | 'flag50'
  | 'grid'
  | 'type'
  | 'access'
  | 'plain'
  | 'browser'
  | 'phone'
  | 'america-by-design'
  | 'america-gov'

type Moment = {
  year: string
  art: HeritageArt
  title: string
  text: string
  source: { title: string; url: string }
  /** Further official pages for the moment. */
  more?: Array<{ title: string; url: string }>
}

export const heritage: { intro: string; moments: Moment[] } = {
  intro:
    'From the first flag to the National Design Studio, the United States has treated clear, recognizable design as part of serving the public.',
  moments: [
    {
      year: '1777',
      art: 'flag13',
      title: 'The Flag Resolution',
      text: 'The Continental Congress resolved that the flag be thirteen stripes, alternate red and white, with thirteen white stars in a blue field.',
      source: { title: 'National Archives', url: 'https://unwritten-record.blogs.archives.gov/2022/06/13/celebrating-flag-day/' },
    },
    {
      year: '1782',
      art: 'seal',
      title: 'The Great Seal',
      text: 'Congress adopted the Great Seal on June 20, 1782, from a design by Secretary Charles Thomson, refined by William Barton.',
      source: {
        title: 'National Archives',
        url: 'https://www.archives.gov/milestone-documents/original-design-of-the-great-seal-of-the-united-states',
      },
    },
    {
      year: '1912',
      art: 'flag48',
      title: 'Proportions standardized',
      text: "A June 24, 1912 executive order from President Taft fixed the flag's proportions and arranged its 48 stars in six rows of eight.",
      source: { title: 'U.S. Government Publishing Office', url: 'https://www.govinfo.gov/content/pkg/BILLS-111hres1429ih/html/BILLS-111hres1429ih.htm' },
    },
    {
      year: '1951',
      art: 'park',
      title: 'The park arrowhead',
      text: 'The Secretary of the Interior authorized the National Park Service arrowhead emblem, formally designated the official symbol in 1962.',
      source: { title: 'National Park Service', url: 'https://home.nps.gov/jaga/history-of-the-nps-arrowhead.htm' },
    },
    {
      year: '1959',
      art: 'flag50',
      title: 'The 50-star flag',
      text: 'Executive Order 10834 set the 50-star design, which became the official flag on July 4, 1960.',
      source: { title: 'National Archives', url: 'https://www.archives.gov/federal-register/codification/executive-order/10834.html' },
    },
    {
      year: '1972',
      art: 'type',
      title: 'Federal Graphics Improvement Program',
      text: 'The National Endowment for the Arts launched a program through which more than 45 federal agencies, including NASA and the Postal Service, redesigned their graphics.',
      source: { title: 'National Endowment for the Arts', url: 'https://www.arts.gov/timeline/1970s/1972' },
    },
    {
      year: '1975',
      art: 'orbit',
      title: 'A space-age identity',
      text: "Richard Danne and Bruce Blackburn designed NASA's logotype, known as the worm, the agency's official logo from 1976 to 1992.",
      source: { title: 'NASA brand guidelines', url: 'https://www.nasa.gov/nasa-brand-center/brand-guidelines/' },
    },
    {
      year: '1977',
      art: 'grid',
      title: 'The Unigrid',
      text: 'The National Park Service enlisted Massimo Vignelli to help create the Unigrid, a graphic system that still standardizes park brochures.',
      source: { title: 'National Park Service', url: 'https://www.nps.gov/subjects/hfc/a-brief-history-of-the-unigrid.htm' },
    },
    {
      year: '1998',
      art: 'access',
      title: 'Section 508 strengthened',
      text: 'Congress amended Section 508 of the Rehabilitation Act, requiring federal agencies to make electronic and information technology accessible to people with disabilities.',
      source: { title: 'Section508.gov', url: 'https://www.section508.gov/manage/laws-and-policies/' },
    },
    {
      year: '2000',
      art: 'browser',
      title: 'FirstGov.gov',
      text: 'FirstGov.gov launched on September 22, 2000, one place to reach federal online information and services. It is known today as USA.gov.',
      source: {
        title: 'The White House, 2000',
        url: 'https://clintonwhitehouse4.archives.gov/textonly/library/hot_releases/Fri_Sep_22_073603_2000.html',
      },
    },
    {
      year: '2010',
      art: 'plain',
      title: 'The Plain Writing Act',
      text: 'The Plain Writing Act of 2010 requires federal agencies to write public documents clearly.',
      source: { title: 'U.S. Government Publishing Office', url: 'https://www.govinfo.gov/app/details/PLAW-111publ274' },
    },
    {
      year: '2014',
      art: 'usds',
      title: 'The U.S. Digital Service',
      text: 'The White House launched the U.S. Digital Service in August 2014 to bring modern design and engineering to the services Americans use.',
      source: {
        title: 'U.S. Digital Service',
        url: 'https://www.usds.gov/',
      },
    },
    {
      year: '2015',
      art: 'uswds',
      title: 'U.S. Web Design System',
      text: '18F and the U.S. Digital Service created USWDS, giving agencies a shared, accessible foundation for federal websites.',
      source: { title: 'U.S. Web Design System', url: 'https://designsystem.digital.gov/' },
    },
    {
      year: '2018',
      art: 'phone',
      title: 'The 21st Century IDEA',
      text: 'The 21st Century IDEA requires new federal websites to be accessible, consistent in appearance, and fully usable on mobile devices.',
      source: { title: 'U.S. Government Publishing Office', url: 'https://www.govinfo.gov/content/pkg/COMPS-15243/pdf/COMPS-15243.pdf' },
    },
    {
      year: '2025',
      art: 'america-by-design',
      title: 'America by Design',
      text: 'An executive order established the America by Design initiative and created the National Design Studio to modernize the interfaces that serve Americans.',
      source: {
        title: 'Improving Our Nation Through Better Design',
        url: 'https://www.whitehouse.gov/presidential-actions/2025/08/improving-our-nation-through-better-design/',
      },
      more: [
        { title: 'America by Design', url: 'https://americabydesign.gov/' },
        { title: 'National Design Studio', url: 'https://ndstudio.gov/' },
      ],
    },
    {
      year: '2026',
      art: 'america-gov',
      title: 'America.gov',
      text: 'America.gov became a single front door to government: describe what you need, and get answers from official sources with clear next steps.',
      source: { title: 'America.gov', url: 'https://america.gov/' },
    },
  ],
}
